import type { DesignTokens } from "../types/theme";

/**
 * ── Apunte Digital · Typographic identity ───────────────────────────────────
 * "Premium digital paper": a statement serif for headings and a
 * legible, warm humanist sans for body text. The stacks include their
 * fallback chain so text remains legible if the font does not
 * load (offline, CDN hiccup).
 */
const APUNTE_SANS =
  "'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const APUNTE_SERIF = "'Fraunces', 'Source Serif 4', Georgia, 'Times New Roman', serif";

/**
 * Base tokens for Light mode — warm ivory + graphite ink, sienna accent.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f6f1e7",
  foreground: "#2b2620",
  surface: "#fdfaf3",
  surfaceForeground: "#2b2620",
  surfaceSecondary: "#f1ebdd",
  surfaceSecondaryForeground: "#2b2620",
  surfaceTertiary: "#e8e0cf",
  surfaceTertiaryForeground: "#2b2620",
  overlay: "#fdfaf3",
  overlayForeground: "#2b2620",
  muted: "#8a8170",
  default: "#efe9db",
  defaultForeground: "#2b2620",
  accent: "#b4541f",
  accentForeground: "#fdfaf3",
  danger: "#b3261e",
  dangerForeground: "#fdfaf3",
  warning: "#b07a16",
  warningForeground: "#2b2620",
  success: "#4f7a3a",
  successForeground: "#fdfaf3",
  separator: "#d8cdb6",
  glassBackground: "rgba(253, 250, 243, 0.82)",
  glassBorder: "rgba(43, 38, 32, 0.1)",
  glassHighlight: "rgba(43, 38, 32, 0.06)",
  glow0: "rgba(180, 84, 31, 0.1)",
  glow1: "rgba(176, 122, 22, 0.08)",
  glow2: "rgba(79, 122, 58, 0.06)",
  mutedForeground: "#6f675a",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(43, 38, 32, 0.08)",
  overlayShadow: "rgba(43, 38, 32, 0.1)",
  fieldShadow: "rgba(43, 38, 32, 0.06)",
  fieldBorder: "rgba(43, 38, 32, 0.1)",
  fieldPlaceholder: "rgba(43, 38, 32, 0.4)",
  fieldBackground: "rgba(43, 38, 32, 0.03)",
  segment: "#fdfaf3",
  segmentForeground: "#2b2620",
  link: "#b4541f",
  focus: "#b4541f",
  fieldForeground: "#2b2620",
  radius: "0.5rem",
  fontNormal: APUNTE_SANS,
  fontMedium: APUNTE_SANS,
  fontSemiBold: APUNTE_SERIF,
  fontBold: APUNTE_SERIF,
  border: "#ddd2bc",
  primary: "#b4541f",
  primaryForeground: "#fdfaf3",
  secondary: "#efe9db",
  secondaryForeground: "#2b2620",
  backdrop: "rgba(43, 38, 32, 0.4)",
};

/**
 * Base tokens for Dark mode — warm graphite (not pure black) + light sienna accent.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#1a1814",
  foreground: "#ede7da",
  surface: "#23201b",
  surfaceForeground: "#ede7da",
  surfaceSecondary: "#2b2722",
  surfaceSecondaryForeground: "#ede7da",
  surfaceTertiary: "#34302a",
  surfaceTertiaryForeground: "#ede7da",
  overlay: "#2b2722",
  overlayForeground: "#ede7da",
  muted: "#a39a88",
  default: "#34302a",
  defaultForeground: "#ede7da",
  accent: "#d98a5c",
  accentForeground: "#1a1814",
  danger: "#e07a6e",
  dangerForeground: "#1a1814",
  warning: "#d9a857",
  warningForeground: "#1a1814",
  success: "#86b56a",
  successForeground: "#1a1814",
  separator: "#3d382f",
  glassBackground: "rgba(26, 24, 20, 0.88)",
  glassBorder: "rgba(237, 231, 218, 0.12)",
  glassHighlight: "rgba(237, 231, 218, 0.2)",
  glow0: "rgba(217, 138, 92, 0.4)",
  glow1: "rgba(217, 168, 87, 0.3)",
  glow2: "rgba(134, 181, 106, 0.24)",
  mutedForeground: "#b8af9c",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.3)",
  overlayShadow: "rgba(0, 0, 0, 0.35)",
  fieldShadow: "rgba(0, 0, 0, 0.25)",
  fieldBorder: "rgba(237, 231, 218, 0.1)",
  fieldPlaceholder: "rgba(237, 231, 218, 0.4)",
  fieldBackground: "rgba(237, 231, 218, 0.03)",
  segment: "#3d382f",
  segmentForeground: "#ede7da",
  link: "#d98a5c",
  focus: "#d98a5c",
  fieldForeground: "#ede7da",
  radius: "0.5rem",
  fontNormal: APUNTE_SANS,
  fontMedium: APUNTE_SANS,
  fontSemiBold: APUNTE_SERIF,
  fontBold: APUNTE_SERIF,
  border: "#3d382f",
  primary: "#d98a5c",
  primaryForeground: "#1a1814",
  secondary: "#34302a",
  secondaryForeground: "#ede7da",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
